import { PhoneField as BasePhoneField, PhoneFieldProps, CountryCodeItem } from '@wheely/ui-kit';
import { useCallback, useMemo } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';
import { useIntl } from 'react-intl';

interface PhoneCode {
  title: JSX.Element;
  value: string;
  validationRules?: UseControllerProps['rules'];
  countryCode: string;
}

interface IProps<TFieldValues extends FieldValues = FieldValues>
  extends UseControllerProps<TFieldValues>,
    Omit<
      React.ComponentProps<typeof BasePhoneField>,
      | 'name'
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'error'
      | 'required'
      | 'items'
      | 'countryCode'
      | 'onCodeChange'
    > {
  namePhoneCode: UseControllerProps<TFieldValues>['name'];
  isRequired?: boolean;
  items: PhoneCode[];
}

const FORBIDDEN_PHONE_NUMBER_CHARACTERS_REGEXP = /[^\d]/g;

const PhoneField = <TFieldValues extends FieldValues>({
  description,
  name,
  namePhoneCode,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  isRequired,
  items,
  ...inputProps
}: IProps<TFieldValues>) => {
  const intl = useIntl();

  const { field: phoneCodeField } = useController({
    name: namePhoneCode,
    control,
    shouldUnregister,
  });

  const validationRules = useMemo(
    () => items.find(item => item.value === phoneCodeField.value)?.validationRules || {},
    [phoneCodeField.value, items],
  );

  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController({
    name,
    rules: {
      required: isRequired
        ? {
            value: true,
            message: intl.formatMessage({
              defaultMessage: 'Field is required.',
              id: 'Q4ipsb',
              description: 'Required field validation message',
            }),
          }
        : undefined,
      ...validationRules,
      ...rules,
    },
    control,
    defaultValue,
    shouldUnregister,
  });

  const handleNumberChange: PhoneFieldProps['onChange'] = useCallback(
    newValue => {
      const filteredValue = newValue.replace(FORBIDDEN_PHONE_NUMBER_CHARACTERS_REGEXP, '');

      field.onChange(filteredValue);
    },
    [field],
  );

  const handleCodeChange = useCallback(
    (countryCodeItem: CountryCodeItem) => {
      phoneCodeField.onChange(countryCodeItem.value);
    },
    [phoneCodeField],
  );

  return (
    <BasePhoneField
      {...field}
      {...inputProps}
      inputRef={ref}
      value={field.value as string}
      error={error?.message}
      description={!error?.message && description ? description : undefined}
      items={items}
      countryCode={phoneCodeField.value as string}
      onCodeChange={handleCodeChange}
      onChange={handleNumberChange}
    />
  );
};

export type { PhoneCode };
export { PhoneField };
